import { LitElement, css, html } from "lit";
import { onceParentAvailable } from "../../utils/widgets.js";
import BgStreets from "../../img/bg_streets.jpg";
import BgAerial from "../../img/bg_aerial.jpg";
import BgMore from "../../img/bg_more.png";
import { BASEMAPS_DEFAULT_IDS } from "../../utils/MapStyleComposer.js";

/**
 * Map Background menu allows user to select background.
 * @class Panoramax.components.menus.MapBackground
 * @element pnx-map-background
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-map-background size="sm" _parent=${viewer} />
 * ```
 */
export default class MapBackground extends LitElement {
	/** @private */
	static styles = [ css`
		:host {
			justify-content: space-evenly;
			box-sizing: border-box;
			display: flex;
			gap: 10px;
		}
		input { display: none; }
		label {
			cursor: pointer;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 3px;
			font-size: 0.8rem;
			color: var(--grey-dark);
		}

		img {
			width: 40px;
			border-radius: 5px;
			vertical-align: middle;
			border: 2px solid var(--widget-bg);
		}

		img.sm { width: 32px; }

		@media screen and (max-width: 576px) {
			label { font-size: 0.9rem; }
		}

		input:checked + label img {
			outline: 3px solid var(--widget-border-btn);
		}

		input:disabled + label {
			opacity: 0.4;
			cursor: not-allowed;
		}

		.select-as-radio {
			position: relative;
		}

		.select-as-radio select {
			opacity: 0;
			position: absolute;
			inset: 0;
		}
	` ];

	/**
     * Component properties.
	 * @memberof Panoramax.components.menus.MapBackground#
     * @type {Object}
     * @property {string} [size=md] The size of the button (sm, md)
     */
	static properties = {
		bg: {state: true},
		size: {type: String},
	};

	constructor() {
		super();
		this.size = "md";
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();

		onceParentAvailable(this).then(() => {
			this._parent.mapStyleComposer?.addEventListener("basemap-changed", e => {
				this.bg = e.detail.basemap;
			});
			this.bg = this._parent.mapStyleComposer?.basemap;
		});
	}

	/** @private */
	_onBgSelect(e) {
		this.bg = e.target.value;
		const bgMore = this.shadowRoot.querySelector("#pnx-map-bg-more");
		if(bgMore) { bgMore.value = null; }
		this._parent?.mapStyleComposer?.switchBasemap(this.bg);
	}

	/** @private */
	render() {
		const parts = [html`
			<input
				type="radio"
				id="bg-streets"
				name="bg"
				value="streets"
				@change=${this._onBgSelect}
				.checked=${this.bg === "streets"}
			/>
			<label for="bg-streets">
				<img src=${BgStreets} class=${this.size} alt="" />
				${this._parent?._t.pnx.map_background_streets}
			</label>
		`];

		if(this._parent.mapStyleComposer.hasStreetsAerialBasemaps()) {
			parts.push(html`
				<input
					type="radio"
					id="bg-aerial"
					name="bg"
					value="aerial"
					@change=${this._onBgSelect}
					.checked=${this.bg === "aerial"}
				/>
				<label for="bg-aerial">
					<img src=${BgAerial} class=${this.size} alt="" />
					${this._parent?._t.pnx.map_background_aerial}
				</label>
			`);
		}

		if(this._parent.mapStyleComposer.hasComplementaryBasemaps()) {
			const layers = Object
				.entries(this._parent.mapStyleComposer.layerRanges.basemaps)
				.filter(([bid,]) => !BASEMAPS_DEFAULT_IDS.includes(bid))
				.map(([bid, b]) => html`
					<option value="${bid}" .selected=${this._parent.mapStyleComposer.basemap === bid}>
						${b.layers[0]?.metadata?.name || bid}
					</option>
				`);
			
			parts.push(html`
				<div class="select-as-radio">
					<input
						type="radio"
						id="bg-more"
						name="bg"
						value="more"
						@change=${this._onBgSelect}
						.checked=${!["aerial", "streets"].includes(this.bg)}
					/>
					<label for="bg-more">
						<img src=${BgMore} class=${this.size} alt="" />
						${this._parent?._t.pnx.map_background_more}
					</label>
					<select id="pnx-map-bg-more" @change=${this._onBgSelect}>
						${layers}
					</select>
				</div>
			`);
		}

		return parts;
	}
}

customElements.define("pnx-map-background", MapBackground);
